import {
  InfiniteData,
  keepPreviousData,
  QueryKey,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { UserStatus } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { SimpleResponse, userQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  RequestConfig,
  useExtendMutation,
} from 'src/queries/queries.utils'
import {
  getOwnUser,
  getUser,
  GetUsersResponse,
  getUsersSearch,
} from 'src/queries/users.network'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  GET_ME_FAILURE,
  GET_ME_INIT,
  GET_ME_SUCCESS,
  postUserWithoutPhoneVerificationAndSignIn,
} from 'src/redux/actions/auth.actions'
import {
  GET_USER_FAILURE,
  GET_USER_INIT,
  GET_USER_SUCCESS,
  GET_USERS_FAILURE,
  GET_USERS_INIT,
  GET_USERS_SUCCESS,
} from 'src/redux/actions/data/users.actions'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'
import { PatchUserRequest } from 'src/requests/patchUser.request'

const selectQueryUserModel = (data: UserListResponse): UserListModel =>
  new UserListModel(data)

interface GetUsersDataModel {
  users: UserListModel[] | undefined
  page: number | undefined
  totalItems: number | undefined
}

const selectUsersSearchListModel = (
  response: InfiniteData<GetUsersResponse, number>,
): GetUsersDataModel => ({
  users: response?.pages.flatMap((page) =>
    page.users.map((user) => new UserListModel(user)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

const getUserForId = (
  userId: string | undefined,
): Promise<UserListResponse> => {
  if (!userId) {
    throw new Error('usersQueries.detail: userId is required')
  }
  return getUser(userId)
}

// queryOptions() factories reuse the existing userQueryKeys so cross-feature
// invalidation (reactQueryInterop.reducer, usePatchUser, useUserDeletePhoneNumber,
// GET_ME_SUCCESS bridge, …) keeps hitting the same cache entries after the
// useExtendQuery retirement. Network functions live in users.network.ts
// (getRequestConfig). The infinite users/search read is inlined in
// useQueryUsersSearch (see the note there) rather than exposed as a factory.
//
// The factories accept the queryFn so the RQ->Redux bridged hooks can bake their
// dispatch wrapper INTO the factory (default = the pure network fn). Overriding
// queryFn on a *spread* of a branded queryOptions() result widens TQueryKey and
// breaks useQuery's overload resolution, so the bridge is injected here instead.
export const usersQueries = {
  me: (queryFn: () => Promise<UserListResponse> = getOwnUser) =>
    queryOptions({
      // userQueryKeys.me() is an `as const` tuple; widen to QueryKey (as
      // userQueryKeys.detail already is) so the branded staleTime/behavior fields
      // don't pin TQueryKey and clash with the caller's spread `...rest` options.
      queryKey: userQueryKeys.me() as QueryKey,
      queryFn,
      select: selectQueryUserModel,
      // TODO: what is an appropriate stale time for the authed user?
      staleTime: 1000 * 60 * 5,
    }),
  detail: (
    userId: string | undefined,
    queryFn: () => Promise<UserListResponse> = () => getUserForId(userId),
  ) =>
    queryOptions({
      queryKey: userQueryKeys.detail(userId),
      queryFn,
      select: selectQueryUserModel,
      enabled: userId !== undefined,
    }),
}

type QueryOwnUserOptions = ExtendBaseQueryOptions<
  UserListResponse,
  UserListModel
>

export const useQueryOwnUser = ({
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryOwnUserOptions) => {
  const dispatch = useDispatch()

  // React Query now owns the primary user/org read. Keep mirroring the raw
  // response into Redux until the remaining auth/bootstrap and locale consumers
  // move off the auth slice; meta.source marks this RQ -> Redux bridge
  // (reactQueryInterop.reducer skips its own invalidate on it). Dispatching
  // inside the queryFn — not a subscriber effect — preserves the legacy
  // once-per-fetch cadence: React Query dedupes the fetch across the many
  // useQueryOwnUser consumers, so Redux still sees one GET_ME cycle per network
  // fetch instead of one per mounted component.
  return useQuery({
    ...usersQueries.me(async () => {
      dispatch({ type: GET_ME_INIT })
      try {
        const data = await getOwnUser()
        dispatch({
          type: GET_ME_SUCCESS,
          payload: data,
          meta: { source: 'react-query' },
        })
        return data
      } catch (error) {
        // Skip clearing auth state when the 401 is PHONE_NOT_VERIFIED — the
        // user just needs to verify their new phone number, not re-login.
        // Clearing the JWT here would trigger cognitoSessionValid → signout.
        // Read the backend error off the raw AxiosError, byte-identical to the
        // legacy useExtendQuery onError (users.network.ts keeps errors
        // un-normalized precisely so this carve-out and its tests still match).
        const errorData = (
          error as { response?: { data?: { error?: string } } }
        )?.response?.data
        if (errorData?.error !== UserStatus.PhoneNotVerified) {
          dispatch({ type: GET_ME_FAILURE, payload: error })
        }
        throw error
      }
    }),
    enabled,
    ...queryCacheOptions,
    ...rest,
  })
}

export const usePatchUser = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    UserListResponse,
    { body: Partial<PatchUserRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/users`,
    onSuccess: () => {
      // invalidate user
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
    },
  })
}

interface QueryUserOptions
  extends ExtendBaseQueryOptions<UserListResponse, UserListModel> {
  userId: string | undefined
}
export const useQueryUser = ({
  userId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryUserOptions) => {
  const dispatch = useDispatch()

  // redux <-> react query interop: mirror the raw response into the data.users
  // slice on each fetch, matching the legacy useExtendQuery bridge. Runs once
  // per network fetch (deduped across subscribers).
  return useQuery({
    ...usersQueries.detail(userId, async () => {
      dispatch({ type: GET_USER_INIT })
      try {
        const data = await getUserForId(userId)
        dispatch({ type: GET_USER_SUCCESS, payload: data })
        return data
      } catch (error) {
        dispatch({ type: GET_USER_FAILURE, payload: error })
        throw error
      }
    }),
    enabled: enabled && userId !== undefined,
    ...queryCacheOptions,
    ...rest,
  })
}

interface QueryUsersSearchOptions
  extends ExtendBaseInfiniteQueryOptions<
    GetUsersResponse,
    number,
    GetUsersDataModel
  > {
  filters: GetUsersSearchRequest
  additionalKey?: string
}

export const useQueryUsersSearch = ({
  filters,
  enabled = true,
  queryCacheOptions,
  additionalKey,
  ...rest
}: QueryUsersSearchOptions) => {
  const dispatch = useDispatch()
  // Inlined rather than spread from a shared infiniteQueryOptions factory: the
  // redux bridge needs a wrapped queryFn, and spreading a *branded*
  // infiniteQueryOptions result while overriding queryFn/select widens
  // TQueryKey and breaks useInfiniteQuery's overload resolution. This reuses the
  // existing userQueryKeys.search key and otherwise matches the retired
  // useExtendInfiniteQuery call verbatim.
  const infiniteQuery = useInfiniteQuery({
    queryKey: userQueryKeys.search(filters, additionalKey),
    // react query <-> redux interop: dispatch the raw page response per fetch
    // (once per network fetch, deduped across subscribers).
    queryFn: async ({ pageParam }) => {
      dispatch({ type: GET_USERS_INIT })
      try {
        const data = await getUsersSearch(filters, pageParam)
        dispatch({ type: GET_USERS_SUCCESS, payload: data })
        return data
      } catch (error) {
        dispatch({ type: GET_USERS_FAILURE, payload: error })
        throw error
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: selectUsersSearchListModel,
    // re-fetches happen in place without resetting back to an empty state, less
    // flashing of content
    placeholderData: keepPreviousData,
    // gcTime: 0 preserves the useExtendInfiniteQuery convention: it prevents
    // React Query from replaying every cached page fetch when the user navigates
    // away from and back to a deep page.
    gcTime: 0,
    enabled: enabled && filters !== undefined,
    ...queryCacheOptions,
    ...rest,
  })

  // Preserve the useExtendInfiniteQuery return contract: consumers read
  // `complete` to know pagination has drained.
  return {
    ...infiniteQuery,
    complete: !infiniteQuery.hasNextPage && !infiniteQuery.isFetching,
  }
}

export const useUserSkipPhoneVerification = (
  email: string,
  password: string,
  acceptOrgInvite?: boolean,
) => {
  const dispatch = useBackgroundDispatch()
  return useExtendMutation<
    { response: SimpleResponse },
    { body: { email: string } }
  >(MutationMethod.POST, {
    mutationUrl: () => 'users/phone/skipverification',
    onSuccess: () => {
      dispatch(
        postUserWithoutPhoneVerificationAndSignIn(
          email,
          password,
          acceptOrgInvite,
        ),
      )
      dispatch(createSetIsExtraVerificationPopupOpen(false))
    },
    requestConfig: RequestConfig.Unauthed,
  })
}

export const useUserDeletePhoneNumber = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<{ response: SimpleResponse }, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => 'users/phone/delete',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
      },
    },
  )
}
